/**
 * SafeImage Component
 * 
 * A drop-in replacement for <img> that gracefully handles:
 * - Missing/deleted images from storage
 * - Network errors during image load
 * - Invalid URLs
 * 
 * Displays a styled placeholder with optional helper text when the image fails to load.
 */
import React, { useState, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { ImageOff, Camera, FileImage, User } from 'lucide-react';

export type PlaceholderType = 'generic' | 'photo' | 'avatar' | 'document' | 'signature';

interface SafeImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
    /** URL of the image to display */
    src: string | null | undefined;
    /** Alt text for accessibility */
    alt: string;
    /** Type of placeholder to show on error */
    placeholderType?: PlaceholderType;
    /** Custom helper text displayed below the icon */
    helperText?: string;
    /** Additional classes for the container (used when showing placeholder) */
    containerClassName?: string;
    /** Callback when image fails to load */
    onImageError?: () => void;
    /** Callback when image loads successfully */
    onImageLoad?: () => void;
    /** Whether to show a loading skeleton before the image loads */
    showLoadingSkeleton?: boolean;
}

const placeholderConfig: Record<PlaceholderType, { Icon: React.ElementType; defaultText: string }> = {
    generic: { Icon: ImageOff, defaultText: 'Image unavailable' },
    photo: { Icon: Camera, defaultText: 'Photo not found' },
    avatar: { Icon: User, defaultText: 'No profile image' },
    document: { Icon: FileImage, defaultText: 'Document unavailable' },
    signature: { Icon: FileImage, defaultText: 'Signature unavailable' },
};

export function SafeImage({
    src,
    alt,
    className,
    containerClassName,
    placeholderType = 'generic',
    helperText,
    onImageError,
    onImageLoad,
    showLoadingSkeleton = false,
    ...props
}: SafeImageProps) {
    const [hasError, setHasError] = useState(false);
    const [isLoading, setIsLoading] = useState(!!src);

    const handleError = useCallback(() => {
        setHasError(true);
        setIsLoading(false);
        onImageError?.();
    }, [onImageError]);

    const handleLoad = useCallback(() => {
        setIsLoading(false);
        onImageLoad?.();
    }, [onImageLoad]);

    // If no src or error occurred, show placeholder
    if (!src || hasError) {
        const { Icon, defaultText } = placeholderConfig[placeholderType];
        const displayText = helperText ?? defaultText;

        return (
            <div
                className={cn(
                    'flex flex-col items-center justify-center bg-muted/50 border border-dashed border-muted-foreground/30 rounded-md',
                    containerClassName,
                    className
                )}
                role="img"
                aria-label={alt}
            >
                <Icon className="h-8 w-8 text-gray-500/50" />
                {displayText && (
                    <span className="mt-2 text-xs text-gray-500/70 text-center px-2">
                        {displayText}
                    </span>
                )}
            </div>
        );
    }

    return (
        <div className={cn('relative', containerClassName)}>
            {/* Loading skeleton overlay */}
            {showLoadingSkeleton && isLoading && (
                <div
                    className={cn(
                        'absolute inset-0 bg-muted/50 animate-pulse rounded-md',
                        className
                    )}
                />
            )}
            <img
                src={src}
                alt={alt}
                className={cn(
                    isLoading && showLoadingSkeleton ? 'opacity-0' : 'opacity-100',
                    'transition-opacity duration-200',
                    className
                )}
                onError={handleError}
                onLoad={handleLoad}
                {...props}
            />
        </div>
    );
}

/**
 * Thumbnail variant with preset sizing for common use cases
 */
interface SafeThumbnailProps extends Omit<SafeImageProps, 'className'> {
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

const thumbnailSizes = {
    xs: 'w-8 h-8',
    sm: 'w-12 h-12',
    md: 'w-16 h-16',
    lg: 'w-24 h-24',
    xl: 'w-32 h-32',
};

export function SafeThumbnail({ size = 'md', ...props }: SafeThumbnailProps) {
    const sizeClass = thumbnailSizes[size];
    return (
        <SafeImage
            {...props}
            className={cn(sizeClass, 'object-cover rounded-md')}
            containerClassName={sizeClass}
        />
    );
}

/**
 * Avatar variant with circular shape
 */
interface SafeAvatarProps extends Omit<SafeImageProps, 'placeholderType' | 'className'> {
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

const avatarSizes = {
    xs: 'w-6 h-6',
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16',
};

export function SafeAvatar({ size = 'md', ...props }: SafeAvatarProps) {
    const sizeClass = avatarSizes[size];
    return (
        <SafeImage
            {...props}
            placeholderType="avatar"
            className={cn(sizeClass, 'object-cover rounded-full')}
            containerClassName={cn(sizeClass, 'rounded-full')}
        />
    );
}

export default SafeImage;
